import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { login as loginService } from 'services/authService';
import { authApi } from 'services/api';
import FormErrorBanner from 'components/errors/FormErrorBanner';
import InlineFieldError from 'components/errors/InlineFieldError';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';
import useSEO from 'hooks/useSEO';

const LOGIN_VIEW_ROUTES = {
  'Dashboard':          '/user-dashboard',
  'Portfolio Overview': '/portfolio-overview',
  'Dividend Calendar':  '/dividend-calendar',
};

// ── Safari-safe localStorage helpers ────────────────────────────────────────
// Safari Private Mode throws SecurityError on localStorage access — these
// wrappers prevent that from crashing the entire login page.
function safeGet(key) { try { return localStorage.getItem(key); } catch { return null; } }
function safeSet(key, v) { try { localStorage.setItem(key, v); } catch {} }
function safeRemove(key) { try { localStorage.removeItem(key); } catch {} }

function getDefaultRoute() {
  // Read from localStorage cache (written by PortfolioDefaultsSection when user saves settings).
  // Do NOT make an API call here — auth state is in the middle of being set up,
  // and a slow/failing API call would block navigation entirely.
  const cached = safeGet('default_login_view');
  return (cached && LOGIN_VIEW_ROUTES[cached]) ? LOGIN_VIEW_ROUTES[cached] : '/user-dashboard';
}

export default function LoginPage() {
  useSEO({
    title: 'Log In',
    description: 'Log in to Safe Dividend Tracker to manage your dividend portfolio, track income, and monitor covered calls and cash-secured puts.',
    canonical: '/login',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login: loginContext, isLoading: authLoading } = useAuth();
  // If redirected here by ProtectedRoute, remember where to go after login.
  // Affiliate pages use ?next= query param; ProtectedRoute uses location.state.from.
  const nextParam = new URLSearchParams(location.search).get('next');
  const intendedDestination = location.state?.from || (nextParam && nextParam.startsWith('/') ? nextParam : null);
  // Guard: prevents the isAuthenticated effect from double-navigating when
  // completeLogin() has already called navigate() after a fresh form submit.
  const hasNavigatedRef = useRef(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const [isNetworkError, setIsNetworkError] = useState(false);

  // Email not verified state
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // 2FA state
  const [twoFaStep, setTwoFaStep] = useState(false);
  const [twoFaTempToken, setTwoFaTempToken] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaError, setTwoFaError] = useState('');
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const twoFaInputRef = useRef(null);

  // Redirect already-authenticated users (e.g. visiting /login while logged in).
  // The hasNavigatedRef guard prevents this from firing AGAIN after a fresh
  // form submit where completeLogin() has already called navigate().
  useEffect(() => {
    if (!authLoading && isAuthenticated && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      // sessionStorage is set by ProtectedRoute — most reliable source
      let ssDest = null;
      try { ssDest = sessionStorage.getItem('after_login_dest'); sessionStorage.removeItem('after_login_dest'); } catch {}
      const destination = ssDest || intendedDestination || getDefaultRoute();
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, intendedDestination]);

  const validateForm = () => {
    const errors = { email: '', password: '' };
    let valid = true;
    if (!email?.trim()) {
      errors.email = 'Email address is required.';
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Please enter a valid email address.';
      valid = false;
    }
    if (!password) {
      errors.password = 'Password is required.';
      valid = false;
    }
    setFieldErrors(errors);
    return valid;
  };

  const completeLogin = async (token, user) => {
    if (rememberMe) safeSet('remember_me', 'true');
    else safeRemove('remember_me');
    // Only update auth state here — let the isAuthenticated useEffect below
    // handle navigation AFTER React commits the state. This prevents
    // ProtectedRoute from seeing isAuthenticated=false on first render.
    loginContext(user, token);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError(null);
    setIsNetworkError(false);
    setEmailNotVerified(false);
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const res = await loginService(email, password);
      if (res?.requires2FA) {
        // 2FA challenge: show OTP input
        setTwoFaTempToken(res.temp_token);
        setTwoFaStep(true);
        setTwoFaCode('');
        setTwoFaError('');
        setTimeout(() => twoFaInputRef.current?.focus(), 100);
      } else if (res?.token && res?.user) {
        await completeLogin(res.token, res.user);
      }
    } catch (err) {
      const isNetwork = !err?.response && err?.request;
      if (isNetwork || err?.code === 'ECONNABORTED') {
        setIsNetworkError(true);
        setFormError('Network error. Check your connection and try again.');
      } else if (err?.response?.status === 403 && err?.response?.data?.details?.code === 'email_not_verified') {
        // Email not verified
        setUnverifiedEmail(err?.response?.data?.details?.email || email);
        setEmailNotVerified(true);
        setFormError(null);
      } else if (err?.response?.status === 401 || err?.response?.status === 422) {
        setFormError('Invalid email or password. Please try again.');
      } else {
        setFormError(err?.response?.data?.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setFormError(null);
    setIsNetworkError(false);
    handleSubmit({ preventDefault: () => {} });
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    try {
      await authApi.resendVerification({ email: unverifiedEmail });
      setResendMessage('Verification email sent! Check your inbox.');
    } catch {
      setResendMessage('Could not send email. Please try again shortly.');
    } finally {
      setResendLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e?.preventDefault();
    setTwoFaError('');
    if (!twoFaCode.trim()) { setTwoFaError('Please enter the verification code.'); return; }
    setTwoFaLoading(true);
    try {
      const res = await authApi.verify2FA({ temp_token: twoFaTempToken, code: twoFaCode.replace(/\s/g, '') });
      const { token, user } = res?.data?.data || {};
      if (token && user) {
        await completeLogin(token, user);
      }
    } catch (err) {
      setTwoFaError(err?.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setTwoFaLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#2E7D32', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      <div className="w-full" style={{ maxWidth: '420px' }}>
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <Logo size={56} />
          </div>
          <h1
            className="text-2xl font-bold mb-1"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}
          >
            Safe Dividend Tracker
          </h1>
          <p
            className="text-sm"
            style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
          >
            Sign in to your account
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-xl border p-6 md:p-8"
          style={{
            backgroundColor: 'var(--color-card)',
            borderColor: 'var(--color-border)',
            boxShadow: '0 4px 24px -4px rgba(0,0,0,0.08)',
          }}
        >
          {/* ── Email not verified ─────────────────────────────────────────── */}
          {emailNotVerified ? (
            <div className="text-center py-2">
              <div
                className="flex items-center justify-center w-14 h-14 rounded-full mx-auto mb-4"
                style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}
              >
                <Icon name="MailWarning" size={28} style={{ color: '#2563eb' }} />
              </div>
              <h2 className="text-base font-semibold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                Please verify your email
              </h2>
              <p className="text-sm mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                We sent a verification link to:
              </p>
              <p className="text-sm font-medium mb-4" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                {unverifiedEmail}
              </p>
              <p className="text-xs mb-5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                Check your inbox and click the link to verify before signing in. Didn&apos;t get it?
              </p>
              {resendMessage && (
                <p className="text-xs mb-3" style={{ color: resendMessage.includes('sent') ? 'var(--color-success)' : 'var(--color-error)', fontFamily: 'var(--font-caption)' }}>
                  {resendMessage}
                </p>
              )}
              <button
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="w-full flex items-center justify-center gap-2 rounded-lg text-sm font-medium border mb-3 transition-all hover:opacity-80 disabled:opacity-60"
                style={{ height: '42px', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', backgroundColor: 'var(--color-muted)', fontFamily: 'var(--font-caption)' }}
              >
                {resendLoading ? <Icon name="Loader2" size={15} className="animate-spin" /> : <Icon name="RefreshCw" size={15} />}
                {resendLoading ? 'Sending…' : 'Resend verification email'}
              </button>
              <button
                onClick={() => { setEmailNotVerified(false); setResendMessage(''); }}
                className="text-sm font-medium"
                style={{ color: '#2E7D32', fontFamily: 'var(--font-caption)' }}
              >
                ← Back to sign in
              </button>
            </div>
          ) : twoFaStep ? (
            /* ── 2FA step ──────────────────────────────────────────────────── */
            <form onSubmit={handle2FASubmit} noValidate>
              <div className="text-center mb-5">
                <div
                  className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-3"
                  style={{ backgroundColor: 'rgba(22,163,74,0.1)' }}
                >
                  <Icon name="ShieldCheck" size={24} style={{ color: 'var(--color-success)' }} />
                </div>
                <h2 className="text-base font-semibold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                  Two-factor authentication
                </h2>
                <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  {useBackupCode
                    ? 'Enter one of your backup codes'
                    : 'Enter the 6-digit code from your authenticator app'}
                </p>
              </div>

              {twoFaError && (
                <div className="mb-4 px-3 py-2 rounded-lg text-sm" style={{ backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', color: 'var(--color-error)', fontFamily: 'var(--font-caption)' }}>
                  {twoFaError}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>
                  {useBackupCode ? 'Backup Code' : 'Authentication Code'}
                </label>
                <input
                  ref={twoFaInputRef}
                  type="text"
                  inputMode={useBackupCode ? 'text' : 'numeric'}
                  maxLength={useBackupCode ? 11 : 6}
                  value={twoFaCode}
                  onChange={(e) => { setTwoFaCode(e.target.value); setTwoFaError(''); }}
                  placeholder={useBackupCode ? 'XXXXX-XXXXX' : '000000'}
                  autoComplete="one-time-code"
                  className="input-base text-center tracking-widest font-mono"
                  style={{ height: '52px', fontSize: '20px', backgroundColor: 'var(--color-background)', borderColor: twoFaError ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', letterSpacing: '0.25em' }}
                />
              </div>

              <button
                type="submit"
                disabled={twoFaLoading}
                className="w-full flex items-center justify-center gap-2 rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-60 mb-4"
                style={{ height: '48px', backgroundColor: '#2E7D32', color: '#FFFFFF', fontFamily: 'var(--font-caption)', fontSize: '1rem' }}
              >
                {twoFaLoading ? <><div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> Verifying…</> : <><Icon name="ShieldCheck" size={18} color="#fff" /> Verify</>}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setTwoFaStep(false); setTwoFaError(''); setTwoFaCode(''); setUseBackupCode(false); }}
                  className="text-sm"
                  style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
                >
                  ← Back
                </button>
                <button
                  type="button"
                  onClick={() => { setUseBackupCode((v) => !v); setTwoFaCode(''); setTwoFaError(''); }}
                  className="text-sm font-medium"
                  style={{ color: '#2E7D32', fontFamily: 'var(--font-caption)' }}
                >
                  {useBackupCode ? 'Use authenticator app' : 'Use backup code'}
                </button>
              </div>
            </form>
          ) : (
            <>
          <FormErrorBanner
            error={formError}
            onDismiss={() => { setFormError(null); setIsNetworkError(false); }}
          />

          {isNetworkError && (
            <div
              className="flex items-center justify-between px-3 py-2 rounded-lg mb-4 text-sm"
              style={{ backgroundColor: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', color: '#EA580C', fontFamily: 'var(--font-caption)' }}
            >
              <span>Connection failed.</span>
              <button onClick={handleRetry} className="underline font-medium ml-2" style={{ color: '#EA580C' }}>Retry</button>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
              >
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e?.target?.value); setFieldErrors((p) => ({ ...p, email: '' })); }}
                placeholder="you@example.com"
                autoComplete="email"
                className="input-base"
                style={{
                  backgroundColor: 'var(--color-background)',
                  borderColor: fieldErrors?.email ? '#EF4444' : 'var(--color-border)',
                  color: 'var(--color-foreground)',
                  height: '48px',
                }}
              />
              <InlineFieldError message={fieldErrors?.email} />
            </div>

            {/* Password */}
            <div className="mb-4">
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e?.target?.value); setFieldErrors((p) => ({ ...p, password: '' })); }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  className="input-base pr-12"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    borderColor: fieldErrors?.password ? '#EF4444' : 'var(--color-border)',
                    color: 'var(--color-foreground)',
                    height: '48px',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity hover:opacity-70"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} color="var(--color-muted-foreground)" />
                </button>
              </div>
              <InlineFieldError message={fieldErrors?.password} />
            </div>

            {/* Remember me + Forgot */}
            <div className="flex items-center justify-between mb-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e?.target?.checked)}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: '#2E7D32' }}
                />
                <span
                  className="text-sm"
                  style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
                >
                  Remember me
                </span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm transition-opacity hover:opacity-80"
                style={{ fontFamily: 'var(--font-caption)', color: '#2E7D32' }}
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ height: '48px', backgroundColor: '#2E7D32', color: '#FFFFFF', fontFamily: 'var(--font-caption)', fontSize: '1rem' }}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Icon name="LogIn" size={18} color="#FFFFFF" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }} />
            <span className="text-xs" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>or</span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'var(--color-border)' }} />
          </div>

          {/* Register CTA */}
          <p
            className="text-center text-sm"
            style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}
          >
            Don&apos;t have an account?{' '}
            <Link
              to="/register"
              className="font-medium transition-opacity hover:opacity-80"
              style={{ color: '#2E7D32' }}
            >
              Create one free
            </Link>
          </p>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
